import React, { useState } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select'
import {
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Eye,
  Star,
  Copy,
  RefreshCw,
  Zap,
  AlertCircle,
} from 'lucide-react'
import {
  useListGenerationsQuery,
  useSelectGenerationMutation,
} from '../app/services/simulationCreatorApi'

type GenerationHistoryProps = {
  simulationId: string
  currentStep?: string
  onRegenerateClick?: () => void
  showRegenerateButton?: boolean
  className?: string
  collapsible?: boolean
  defaultCollapsed?: boolean
}

const GenerationHistory: React.FC<GenerationHistoryProps> = ({
  simulationId,
  currentStep,
  onRegenerateClick,
  showRegenerateButton = true,
  className = '',
  collapsible = false,
  defaultCollapsed = true,
}) => {
  const [selectedGenerationId, setSelectedGenerationId] = useState<string | null>(null)
  const [expandedGeneration, setExpandedGeneration] = useState<string | null>(null)
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed)

  const { data: generationsData, isLoading, error, refetch } = useListGenerationsQuery({
    simulationId,
    step: currentStep,
  }, {
    pollingInterval: 5000, // Poll every 5 seconds for updates
    skip: !simulationId, // Skip if no simulation ID
  })

  const [selectGeneration, { isLoading: isSelecting }] = useSelectGenerationMutation()

  const generations = generationsData?.generations || []

  const handleSelectGeneration = async (generationId: string) => {
    try {
      await selectGeneration({
        simulationId,
        generationId,
      }).unwrap()
      
      setSelectedGenerationId(generationId)
      
      // Don't auto-progress - let user review and compare in main UI
      // const generation = generations.find(g => g.id === generationId)
      // if (generation && onGenerationSelect) {
      //   onGenerationSelect(generation)
      // }
    } catch (error) {
      console.error('Failed to select generation:', error)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'FAILED':
        return <XCircle className="h-4 w-4 text-red-600" />
      case 'IN_PROGRESS':
        return <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
      case 'PENDING':
        return <Clock className="h-4 w-4 text-yellow-600" />
      default:
        return <Clock className="h-4 w-4 text-gray-400" />
    }
  }


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const formatCost = (cost: number | string | null | undefined) => {
    if (!cost) return '$0.0000'
    const numCost = typeof cost === 'string' ? parseFloat(cost) : cost
    return `$${numCost.toFixed(4)}`
  }

  const formatDuration = (ms: number) => {
    return `${(ms / 1000).toFixed(1)}s`
  }


  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const toggleExpanded = (generationId: string) => {
    setExpandedGeneration(expandedGeneration === generationId ? null : generationId)
  }

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Generation History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading generations...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            Generation History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">Failed to load generation history</p>
            <Button variant="outline" onClick={() => refetch()}>
              <span className="flex items-center">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </span>
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle 
          className={`flex items-center gap-2 ${collapsible ? 'cursor-pointer' : ''}`}
          onClick={collapsible ? () => setIsCollapsed(!isCollapsed) : undefined}
        >
          <Clock className="h-5 w-5" />
          Generation History
          {collapsible && (
            <span className={`transform transition-transform ${isCollapsed ? '' : 'rotate-90'}`}>
              ▶
            </span>
          )}
        </CardTitle>
        {!isCollapsed && (
          <CardDescription>
            {generations.length === 0 
              ? 'No generations yet. Generate content to see history.'
              : `${generations.length} generation${generations.length !== 1 ? 's' : ''} available`
            }
          </CardDescription>
        )}
      </CardHeader>
      {!isCollapsed && (
        <CardContent>
        {generations.length === 0 ? (
          <div className="text-center py-8">
            <Zap className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">
              No generations yet for this step
            </p>
            {showRegenerateButton && onRegenerateClick && (
              <Button onClick={onRegenerateClick}>
                <span className="flex items-center">
                  <Zap className="h-4 w-4 mr-2" />
                  Generate Content
                </span>
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Quick selection dropdown */}
            <div className="space-y-2 pb-2 border-b">
              <Select
                value={selectedGenerationId || ''}
                onValueChange={handleSelectGeneration}
                disabled={isSelecting}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a generation to use..." />
                </SelectTrigger>
                <SelectContent>
                  {generations.map((generation) => (
                    <SelectItem key={generation.id} value={generation.id}>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(generation.status)}
                        <span className="flex-1">
                          {formatDate(generation.createdAt)}
                        </span>
                        {generation.isSelected && (
                          <Star className="h-4 w-4 text-yellow-500 ml-2" />
                        )}
                        {generation.isDuplicate && (
                          <Copy className="h-4 w-4 text-gray-400 ml-1" />
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {showRegenerateButton && onRegenerateClick && (
                <Button variant="outline" onClick={onRegenerateClick} className="w-full">
                  <span className="flex items-center">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Generate New
                  </span>
                </Button>
              )}
            </div>

            {/* Generation list */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {generations.map((generation) => (
                <div
                  key={generation.id}
                  className={`p-4 rounded-lg transition-all ${
                    generation.isSelected
                      ? 'bg-blue-50 border-l-4 border-l-blue-500'
                      : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 flex-1">
                      {getStatusIcon(generation.status)}
                      <span className="font-medium text-sm">
                        {formatDate(generation.createdAt)}
                      </span>
                      {generation.isSelected && (
                        <Badge variant="default" className="text-xs">
                          <span className="flex items-center">
                            <Star className="h-3 w-3 mr-1" />
                            Selected
                          </span>
                        </Badge>
                      )}
                      {generation.isDuplicate && (
                        <Badge variant="secondary" className="text-xs">
                          <span className="flex items-center">
                            <Copy className="h-3 w-3 mr-1" />
                            Duplicate
                          </span>
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleExpanded(generation.id)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
                    {generation.aiProvider && (
                      <span>{generation.aiProvider}</span>
                    )}
                    {generation.aiModel && (
                      <span>{generation.aiModel}</span>
                    )}
                    {generation.tokensUsed && (
                      <span>{generation.tokensUsed.toLocaleString()} tokens</span>
                    )}
                    {generation.estimatedCost && (
                      <span>{formatCost(generation.estimatedCost)}</span>
                    )}
                    {generation.processingTimeMs && (
                      <span>{formatDuration(generation.processingTimeMs)}</span>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-2">
                    {([] as React.ReactNode[]).concat(
                      generation.status === 'COMPLETED' && !generation.isSelected ? (
                        <Button
                          key="select"
                          variant="outline"
                          size="sm"
                          onClick={() => handleSelectGeneration(generation.id)}
                          disabled={isSelecting}
                        >
                          {isSelecting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <span className="flex items-center">
                              <Star className="h-4 w-4 mr-1" />
                              Select
                            </span>
                          )}
                        </Button>
                      ) : [],
                      generation.generatedContent ? (
                        <Button
                          key="copy"
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(JSON.stringify(generation.generatedContent, null, 2))}
                        >
                          <span className="flex items-center">
                            <Copy className="h-4 w-4 mr-1" />
                            Copy
                          </span>
                        </Button>
                      ) : []
                    )}
                  </div>

                  {/* Expanded content */}
                  {expandedGeneration === generation.id && (
                    <div className="mt-3 pt-3 border-t">
                      {([] as React.ReactNode[]).concat(
                        generation.prompt ? (
                          <div key="prompt" className="mb-3">
                            <h5 className="font-medium text-sm mb-1">Prompt:</h5>
                            <p className="text-sm text-muted-foreground bg-gray-50 p-2 rounded">
                              {generation.prompt}
                            </p>
                          </div>
                        ) : [],
                        generation.errorMessage ? (
                          <div key="error" className="mb-3">
                            <h5 className="font-medium text-sm mb-1 text-red-600">Error:</h5>
                            <p className="text-sm text-red-600 bg-red-50 p-2 rounded">
                              {generation.errorMessage}
                            </p>
                          </div>
                        ) : [],
                        generation.generatedContent ? (
                          <div key="content">
                            <h5 className="font-medium text-sm mb-1">Generated Content:</h5>
                            <div className="bg-gray-50 p-3 rounded text-xs font-mono max-h-40 overflow-y-auto">
                              <pre>{JSON.stringify(generation.generatedContent, null, 2)}</pre>
                            </div>
                          </div>
                        ) : []
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        </CardContent>
      )}
    </Card>
  )
}

export default GenerationHistory